import type { FC, ReactNode } from 'react'
import type { Bilingual } from '@/types/content'

import Link from 'next/link'
import { ArrowRight, LayoutGrid, Network } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/hooks/use-locale'
import LotusMark from '@/components/generals/lotus-mark'

const heading: Bilingual = {
    en: "Go deeper into the Padma Purana",
    hi: "पद्मपुराण में और गहरे उतरिए"
}

const body: Bilingual = {
    en: "Read every parva point by point, with the people and places that fill it, or see how the houses of Rama, Ravana, Sugriva and Hanuman are bound together in the family tree.",
    hi: "हर पर्व को उसके पात्रों और स्थानों सहित बिंदुवार पढ़िए, या वंशवृक्ष में देखिए कि राम, रावण, सुग्रीव और हनुमान के कुल आपस में कैसे जुड़े हैं।"
}

const StoryCta: FC = (): ReactNode => {
    const { locale, t, tx } = useLocale()
    const latin = locale === 'en'

    return (
        <section aria-labelledby='story-next-title' className='relative'>
            <div aria-hidden='true' className='gold-rule absolute inset-x-0 top-0' />

            <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24'>
                <div className='night-sky relative isolate overflow-hidden rounded-3xl px-6 py-12 text-white shadow-[0_30px_80px_-50px_rgb(40_20_60/0.8)] sm:px-12 sm:py-16 lg:px-16 lg:py-20'>
                    <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                        <div className='absolute -right-40 top-1/2 size-[34rem] -translate-y-1/2 rounded-full border border-gold/10 sm:-right-24' />
                        <div className='animate-slow-spin absolute -right-20 top-1/2 size-[24rem] -translate-y-1/2 rounded-full border border-dashed border-gold/20 sm:-right-4' />
                        <LotusMark className='absolute -right-8 top-1/2 size-[16rem] -translate-y-1/2 opacity-[0.1] sm:right-8 sm:opacity-[0.16]' />
                        <div className='absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-vermilion/20 to-transparent' />
                        <div className='absolute inset-3 rounded-2xl border border-gold/15 sm:inset-4' />
                    </div>

                    <div className='max-w-2xl'>
                        <LotusMark className='size-12' />

                        <h2 id='story-next-title' className={cn('mt-5 font-heading text-4xl font-semibold text-white sm:text-5xl', latin ? 'leading-[1.08]' : 'leading-snug')}>
                            {tx(heading)}
                        </h2>

                        <div aria-hidden='true' className='mt-6 h-px w-32 bg-linear-to-r from-gold to-transparent' />

                        <p className={cn('mt-6 text-base text-white/75 sm:text-lg', latin ? 'leading-relaxed' : 'leading-[1.85]')}>
                            {tx(body)}
                        </p>

                        <div className='mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap'>
                            <Link
                                href='/parva'
                                className='group inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-gold px-6 py-2 text-base font-semibold text-night shadow-[0_10px_40px_-10px_rgb(233_185_73/0.7)] transition-colors hover:bg-gold-soft focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                            >
                                <LayoutGrid className='size-5' aria-hidden='true' />
                                {t.browseChapters}
                                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none' aria-hidden='true' />
                            </Link>

                            <Link
                                href='/lineage'
                                className='inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-2 text-base font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                            >
                                <Network className='size-5' aria-hidden='true' />
                                {t.exploreTree}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default StoryCta
